import { cn } from '@/lib/utils';
import { useBanners } from '@/features/banners/banners';

/** 고정 배너 이미지. 1082×1454 (세로 0.74:1) — 폭 260px 일 때 349px 높이. */
// 260px 폭에 그린다 — 540w WebP(106KB) 면 2x 까지 충분하다. 원본 jpg 는 1082w 556KB 였다.
const BANNER_SRC = 'https://cdn.zoxo.kr/assets/orchard-banner-540.webp';
/** 문구가 그림에 구워져 있다. 화면 낭독기에는 이쪽이 유일한 내용이다. */
const BANNER_ALT =
  '줘도 못먹나? 2025년도 지원사업 1년 집행 총예산 3조 2,940억원. ' +
  '토론·질문·멘토링은 좋소 과수원에서.';

interface OffsideBannerProps {
  className?: string;
}

/**
 * 본문 오른쪽에 붙어 고정되는 배너 (CGV 방식).
 *
 * 기준이 뷰포트가 아니라 **본문 칼럼**이다. 본문은 56rem(896px) 가운데 정렬
 * 이므로 오른쪽 모서리가 `50% + 28rem` 이고, 배너는 거기서 1.5rem 띄운
 * `50% + 29.5rem` 에서 시작한다. 창을 넓혀도 본문과의 간격이 24px 로 유지된다
 * — `right-0` 계열로 붙이면 넓힐수록 본문에서 멀어져 관계가 끊긴다.
 *
 * 폭 260px / 최소 표시 1464px 은 맥북 14인치(뷰포트 1512px)에서 보이도록 잡은
 * 값이다. 그 화면의 본문 오른쪽 끝이 1204px 이라 남는 공간이 308px 이고,
 * 본문과의 간격 24px 을 빼면 배너에 쓸 수 있는 폭이 284px 이다. 여기서 화면
 * 오른쪽에도 같은 24px 을 남겨 260px 으로 정했다.
 *
 * 최소 표시 폭은 배너 오른쪽 끝(`50% + 29.5rem + 260px = 50% + 732px`)이
 * 화면 안에 들어오는 조건에서 나온다 — 732 ≤ 뷰포트/2, 즉 1464px.
 * **폭이나 간격을 바꾸면 이 기준도 함께 다시 계산해야 한다.**
 */
export function OffsideBanner({ className }: OffsideBannerProps) {
  // 콘솔이 올린 그림이 있으면 그것, 없으면 기본. SSR · SPA 가 같은 컨텍스트를 읽는다.
  const uploaded = useBanners().offside;
  return (
    <div
      className={cn(
        // 폭을 래퍼에 못박는다. fixed 요소는 폭을 안 주면 남은 공간만큼만
        // 줄어들고, 전역 `img { max-width: 100% }` 가 이미지를 그 폭으로 깎아
        // 화면에 따라 크기가 달라진다.
        'fixed bottom-6 left-[calc(50%+29.5rem)] z-10 hidden w-[260px] min-[1464px]:block',
        className,
      )}
    >
      <img
        src={uploaded?.url ?? BANNER_SRC}
        alt={BANNER_ALT}
        width={uploaded?.width ?? 540}
        height={uploaded?.height ?? 726}
        loading="lazy"
        className="w-full rounded-lg"
      />
    </div>
  );
}
